import React from 'react';
import { Layout, routes, siteName, pageTypes, AdMobileAnchor, AdMobileBanner, AdLeaderboard, AdVideo } from 'gatsby-theme-apollo';
import TierListPreviewCards from '../cards/tier-list-preview-card';
import PostPreviewCards from '../cards/post-preview-card';
import PatchPreviewCards from '../cards/patch-preview-card';
import Footer from './footer';
import GameContent from './content';
import EmptyState from '../status/empty-state';
import CTA from '../cta';

import Section from '../layout/section';
import EntityList, { StyledEntityList, EntityThumbnailLink } from '../entities/entity-list';
import { sortPostsByDate } from '../../util/sort';
import { MyTierLists, MyPosts } from '../content/my-content';

/**
  Renders Game Hub pages that aggregate subset of content types at 
  /game-name
*/
const GameHubPage = ({ location, pageContext }) => {
  const pageType = pageTypes.GAME_HUB;
  const {
    posts,
    entitySets,
    featuredTierListPreviews,
    gameName,
    patches,
    gameModes,
    gameModeTerm,
    EntityConfigs
  } = pageContext;

  const pages =
    posts &&
    posts.filter(post => {
      return post.page;
    });

  let news = posts && sortPostsByDate(posts);
  news = news.filter(post => !post.page);

  const pageTitle = `${gameName} Wiki | ${siteName}`;
  const pageDescription = `Your updated ${gameName} wiki with tier lists, news, patch notes, guides, tips, cheat sheets, and more.`;

  return (
    <Layout
      location={location}
      gameName={gameName}
      title={pageTitle}
      description={pageDescription}
      pageType={pageType}
    >
      <AdMobileAnchor placementId={'gh-anchor-' + pageType +'-bottom'} anchorType='bottom' offset='47' />
      <GameContent header={`${gameName} Wiki`} showTopShareBar>
        <AdMobileBanner placementId={"gh-mobilebanner-" + pageType + "-top"} />
        <AdLeaderboard placementId={"gh-leaderboard-" + pageType + "-top"} />
        <MyTierLists />
        <>
          {featuredTierListPreviews && featuredTierListPreviews.length !== 0 ? (
            <>
              <Section
                title="Featured Tier Lists"
                fullWidth
                linkTarget={routes.TierListHub(gameName)}
                linkText={`All Tier Lists`}
              >
                <TierListPreviewCards
                  tierListPreviews={featuredTierListPreviews}
                  limit={3}
                  disableShowMore
                />
              </Section>
            </>
          ) : (
            <EmptyState>
              No featured {gameName} Tier Lists have been created yet. You can be the first!
            </EmptyState>
          )}
          {EntityConfigs &&
            EntityConfigs.map(term => {
              return (
                <Section
                  fullWidth
                  title={term.plural}
                  linkTarget={routes.EntityHub(gameName, term.plural)}
                  linkText={`All ${term.plural}`}
                  key={term.plural}
                >
                  <EntityList
                    fullWidth
                    entities={entitySets[term.plural]}
                    gameName={gameName}
                    entityType={term.plural}
                    limit={10}
                  />
                </Section>
              );
            })}

          <MyPosts />
          {((news && news.length > 0) || (pages && pages.length > 0)) && (
            <Section
              fullWidth
              title={'News and Guides'}
              linkTarget={routes.PostHub(gameName)}
              linkText="All News & Guides"
            >
              <PostPreviewCards postPreviews={news} limit={6} disableShowMore />
              <PostPreviewCards postPreviews={pages} limit={6} disableShowMore />
            </Section>
          )}

          {patches && patches.length > 0 && (
            <Section
              fullWidth
              title={`Patch Notes`}
              linkTarget={patches.length > 3 ? routes.PatchHub(gameName) : null}
              linkText={'All Patch Notes'}
            >
              <PatchPreviewCards patchPreviews={patches} limit={3} disableShowMore />
            </Section>
          )}

          {gameModes && gameModes.length > 0 && (
            <Section title={gameModeTerm || 'Game Modes'} fullWidth>
              <StyledEntityList>
                {gameModes.map(mode => {
                  return (
                    <EntityThumbnailLink
                      to={routes.GameMode(gameName, mode.name)}
                      thumbnailURI={mode.iconURI}
                      name={mode.name}
                      key={mode.name}
                    />
                  );
                })}
              </StyledEntityList>
            </Section>
          )}
        </>
        <Footer>
          <AdVideo placementId={'gh-video-' + pageType + '-floating'} />
          <CTA campaignKey="Discord" />
        </Footer>
      </GameContent>
    </Layout>
  );
};

export default GameHubPage;
